import React, { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Edit, Plus, Search, Trash2, Upload } from 'lucide-react';

import {
	useDeleteRevenueConfigurationMutation,
	useGetRevenueConfigurationsQuery,
} from '../api';
import { useListPartnersQuery } from '../api/partners';
import { useFeature } from '../hooks/useFeature';
import { useAuth } from '../providers/AppAuthProvider';

import Loader from './Loader';
import PricingLimitsCard from './settings/PricingLimitsCard';
import { formatShortDate, v9 } from './settings/v9';

interface RevenueConfigListProps {
	onSelectConfig?: (configName: string) => void;
	onCreateNew?: () => void;
}

const tierLabel = (tier: string) => (tier === 'OTHER' ? 'Other' : tier);

const REVENUE_COLUMNS = [
	'Configuration name',
	'Partner',
	'Risk tiers',
	'Partner share',
	'Status',
	'Updated',
	'Actions',
];
// Shown only to tenants with the partner_programs feature (BASYS).
const PROGRAM_COLUMNS = ['Partner', 'Partner share'];

const RevenueConfigList: React.FC<RevenueConfigListProps> = ({
	onSelectConfig,
	onCreateNew,
}) => {
	const navigate = useNavigate();
	const { isAdmin } = useAuth();
	const [search, setSearch] = useState('');
	const [partner, setPartner] = useState('');

	const handleSelectConfigInternal = (configId: string) => {
		if (onSelectConfig) {
			onSelectConfig(configId);
		} else {
			navigate(`/settings?tab=revenue&edit=${configId}`);
		}
	};

	const handleCreateNewInternal = () => {
		if (onCreateNew) {
			onCreateNew();
		} else {
			navigate('/settings?tab=revenue&create=true');
		}
	};

	const {
		data: configsResponse,
		isLoading,
		error,
		refetch,
	} = useGetRevenueConfigurationsQuery();

	const [deleteConfig, { isLoading: isDeleting }] =
		useDeleteRevenueConfigurationMutation();

	const configEntries = useMemo(
		() =>
			Object.entries(configsResponse?.data ?? {}).filter(
				([, configs]) => configs && configs.length > 0,
			),
		[configsResponse],
	);
	const partnerPrograms = useFeature('partner_programs');
	const { data: allPartners = [] } = useListPartnersQuery(undefined, {
		skip: !partnerPrograms,
	});
	// A partner links to its Schedule A by name (partners.revenue_config_name).
	const partnersBySchedule = useMemo(() => {
		const byName = new Map<string, string[]>();
		for (const p of allPartners) {
			if (p.revenue_config_name) {
				byName.set(p.revenue_config_name, [
					...(byName.get(p.revenue_config_name) ?? []),
					p.name,
				]);
			}
		}
		return byName;
	}, [allPartners]);
	const partnerNames = useMemo(
		() => [...new Set([...partnersBySchedule.values()].flat())].sort(),
		[partnersBySchedule],
	);
	const rows = useMemo(() => {
		const query = search.trim().toLowerCase();
		return configEntries.filter(([name]) => {
			const rowPartners = partnersBySchedule.get(name) ?? [];
			return (
				(!query ||
					name.toLowerCase().includes(query) ||
					rowPartners.some((p) => p.toLowerCase().includes(query))) &&
				(!partner || rowPartners.includes(partner))
			);
		});
	}, [configEntries, search, partner, partnersBySchedule]);

	const handleDelete = async (configId: string, configName: string) => {
		if (
			window.confirm(
				`Are you sure you want to delete the revenue configuration "${configName}"? This will delete all 3 risk tier configurations.`,
			)
		) {
			try {
				await deleteConfig(configId).unwrap();
				refetch();
			} catch (error) {
				console.error('Failed to delete config:', error);
				alert('Failed to delete configuration. Please try again.');
			}
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader size="xl" text="Loading revenue configurations..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="text-center">
					<div className="text-error-600 text-lg mb-4">
						Error loading revenue configurations
					</div>
					<p className="text-neutral-600">Please try again later.</p>
				</div>
			</div>
		);
	}

	const adminTitle = (title: string) =>
		isAdmin ? title : 'Admin access required';

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-6">
				<div>
					<div className={v9.title}>Revenue Configurations</div>
					<div className={v9.subtitle}>
						Schedule A buy rates and partner revenue share, one per partner
					</div>
				</div>
				<div className="flex flex-shrink-0 flex-wrap items-center gap-3">
					<button
						type="button"
						className={v9.btnSecondary}
						disabled
						title="Import is not available yet"
					>
						<Upload className={v9.icon} />
						Import templates
					</button>
					<button
						type="button"
						className={v9.btnPrimary}
						onClick={handleCreateNewInternal}
						disabled={!isAdmin}
						title={adminTitle('Create new revenue configuration')}
					>
						<Plus className={v9.icon} />
						New configuration
					</button>
				</div>
			</div>

			<PricingLimitsCard />

			<div className={v9.card}>
				<div className={v9.cardHead}>
					<div className="relative w-[360px] max-w-full">
						<Search className={v9.searchIcon} />
						<input
							className={v9.searchInput}
							placeholder="Search configurations or partners…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							aria-label="Search configurations"
						/>
					</div>
					{partnerPrograms && (
						<select
							className={`${v9.filterSelect} w-[210px]`}
							value={partner}
							onChange={(e) => setPartner(e.target.value)}
							aria-label="Partner"
						>
							<option value="">All partners</option>
							{partnerNames.map((p) => (
								<option key={p}>{p}</option>
							))}
						</select>
					)}
				</div>

				<div className="mt-4 overflow-x-auto">
					<table className="w-full border-collapse">
						<thead>
							<tr>
								{(partnerPrograms
									? REVENUE_COLUMNS
									: REVENUE_COLUMNS.filter((c) => !PROGRAM_COLUMNS.includes(c))
								).map((heading) => (
									<th key={heading} className={v9.th}>
										{heading}
									</th>
								))}
							</tr>
						</thead>
						<tbody className={v9.tbody}>
							{rows.map(([name, configs]) => {
								const first = configs[0];
								const share = first.partner_revenue_share_percent;
								return (
									<tr
										key={first.id}
										className="cursor-pointer hover:bg-gray-50"
										onClick={() => handleSelectConfigInternal(first.id)}
									>
										<td className={v9.tdPrimary}>{name}</td>
										{partnerPrograms && (
											<td className={v9.tdWrap}>
												{partnersBySchedule.get(name)?.join(', ') || '—'}
											</td>
										)}
										<td className={v9.tdWrap}>
											{configs.map((c) => tierLabel(c.risk_tier)).join(' · ')}
										</td>
										{partnerPrograms && (
											<td className={v9.td}>
												{share != null ? `${share}%` : '—'}
											</td>
										)}
										<td className={v9.td}>
											<span
												className={`${v9.pill} ${first.is_active ? v9.pillSuccess : v9.pillNeutral}`}
											>
												{first.is_active
													? partnerPrograms
														? 'Default'
														: 'Active'
													: partnerPrograms
														? 'Custom'
														: 'Inactive'}
											</span>
										</td>
										<td className={v9.tdMuted}>
											{formatShortDate(first.updated_at)}
										</td>
										<td className={v9.td}>
											<span className="flex items-center gap-2">
												<button
													type="button"
													className={v9.btnSm}
													onClick={(e) => {
														e.stopPropagation();
														handleSelectConfigInternal(first.id);
													}}
													disabled={!isAdmin}
													title={adminTitle('Edit configuration')}
												>
													<Edit className={v9.iconSm} />
													Edit
												</button>
												<button
													type="button"
													className={`${v9.btnSm} !text-red-700`}
													onClick={(e) => {
														e.stopPropagation();
														handleDelete(first.id, name);
													}}
													disabled={isDeleting || !isAdmin}
													title={adminTitle('Delete configuration')}
													aria-label="Delete configuration"
												>
													<Trash2 className={v9.iconSm} />
												</button>
											</span>
										</td>
									</tr>
								);
							})}
							{rows.length === 0 && (
								<tr>
									<td colSpan={partnerPrograms ? 7 : 5} className={v9.tdMuted}>
										No configuration matches. Clear the search or pick another
										partner.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className={v9.tableFoot}>
					<span>
						Showing {rows.length} of {configEntries.length} configurations
					</span>
				</div>
			</div>
		</div>
	);
};

export default RevenueConfigList;
